import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Box,
    Switch,
    Spinner,
    useToast,
    Button,
    Input,
    Textarea,
    IconButton,
} from "@chakra-ui/react";
import { FaSave, FaImage } from "react-icons/fa";
import EditProductAddons from "./EditProductAddons"; // Import the new component

const EditStoreProductsTable = ({ id }) => {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewProductId, setViewProductId] = useState(null); // State to manage viewed product ID
    const [viewProductDescription, setViewProductDescription] = useState(null);
    const [editingFields, setEditingFields] = useState({}); // State to manage editing fields
    const [updatingProducts, setUpdatingProducts] = useState(new Set()); // Track which products are being updated
    const toast = useToast();

    // Extract fetchProducts function outside useEffect
    const fetchProducts = async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.post(
                `/dummy/get-products`,
                { store_id: id }
            );
            // Add these debug lines:
            console.log('get-products response:', response);
            console.log('get-products response.data:', response.data);
            console.log('get-products response.data.status:', response.data.status);

            if (response.data.status === "success") {
                setProducts(response.data.data);
            } else {
                toast({
                    title: "Error fetching products",
                    description: response.data.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to fetch products",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Update useEffect to call the extracted function
    useEffect(() => {
        fetchProducts();
    }, [id, toast]);

    const handleToggle = async (productId, currentStatus) => {
        try {
            const response = await axiosInstance.post(
                `/dummy/toggle-product-active`,
                { wp_product_id: Number(productId), active_yn: !currentStatus }
            );
            if (response.data.status === "Success") {
                setProducts(
                    products.map((product) =>
                        product.wp_product_id === productId
                            ? { ...product, activeyn: !currentStatus }
                            : product
                    )
                );
                toast({
                    title: `Product status set to ${response.data.dataone}`,
                    description: response.data.message,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Error toggling product active status",
                    description: response.data.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to fetch products",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleViewClick = (productId, description) => {
        setViewProductId(productId); // Set the viewed product ID
        setViewProductDescription(description);
    };

    // Update handleBackClick to refresh the product list
    const handleBackClick = async () => {
        setViewProductId(null); // Reset the viewed product ID to show the products table
        await fetchProducts(); // Refresh the product list
    };

    const handleFieldChange = (productId, field, value) => {
        setEditingFields(prev => ({
            ...prev,
            [`${productId}_${field}`]: value
        }));
    };

    const handleSaveProduct = async (product) => {
        const productId = product.wp_product_id;
        setUpdatingProducts(prev => new Set(prev).add(productId));

        try {
            const updatedData = {
                wp_product_id: productId,
                wp_description_display1: editingFields[`${productId}_wp_description_display1`] ?? product.wp_description_display1,
                wp_description_display2: editingFields[`${productId}_wp_description_display2`] ?? product.wp_description_display2,
                charge: editingFields[`${productId}_charge`] ?? product.charge,
                category: editingFields[`${productId}_category`] ?? product.category,
            };

            const response = await axiosInstance.post(
                `/dummy/update-product`,
                updatedData
            );

            if (response.data.success === true) {
                // Update the products state with the new values
                setProducts(prev => prev.map(p => 
                    p.wp_product_id === productId 
                        ? { ...p, ...updatedData }
                        : p
                ));

                // Clear the editing fields for this product
                setEditingFields(prev => {
                    const newState = { ...prev };
                    delete newState[`${productId}_wp_description_display1`];
                    delete newState[`${productId}_wp_description_display2`];
                    delete newState[`${productId}_charge`];
                    delete newState[`${productId}_category`];
                    return newState;
                });

                toast({
                    title: "Product updated successfully",
                    description: response.data.message,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Error updating product",
                    description: response.data.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update product",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUpdatingProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        }
    };

    const hasChanges = (productId) => {
        return editingFields[`${productId}_wp_description_display1`] !== undefined ||
               editingFields[`${productId}_wp_description_display2`] !== undefined ||
               editingFields[`${productId}_charge`] !== undefined ||
               editingFields[`${productId}_category`] !== undefined;
    };

    const getFieldValue = (fieldKey, fallbackValue) => {
        return editingFields[fieldKey] ?? fallbackValue ?? '';
    };

    if (isLoading) {
        return <Spinner size="lg" />;
    }

    if (viewProductId) {
        return (
            <EditProductAddons
                productId={viewProductId}
                productDescription={viewProductDescription}
                onBack={handleBackClick}
            />
        );
    }

    return (
        <Box>
            <Table variant="simple" size="sm">
                <Thead>
                    <Tr>
                        <Th>Product ID</Th>
                        <Th>Description 1</Th>
                        <Th>Description 2</Th>
                        <Th>Category</Th>
                        <Th>Image</Th>
                        <Th>Charge</Th>
                        <Th>Active</Th>
                        <Th>Save</Th>
                        <Th>View</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {products.map((product) => (
                        <Tr key={product.id}>
                            <Td>{product.id}</Td>
                            <Td>
                                <Input
                                    size="sm"
                                    value={editingFields[`${product.wp_product_id}_wp_description_display1`] ?? product.wp_description_display1 ?? ''}
                                    onChange={(e) => handleFieldChange(product.wp_product_id, 'wp_description_display1', e.target.value)}
                                    placeholder="Description 1"
                                />
                            </Td>
                            <Td width="600px">
                                <Textarea
                                    size="sm"
                                    resize="vertical"
                                    minHeight="60px"
                                    value={editingFields[`${product.wp_product_id}_wp_description_display2`] ?? product.wp_description_display2 ?? ''}
                                    onChange={(e) => handleFieldChange(product.wp_product_id, 'wp_description_display2', e.target.value)}
                                    placeholder="Description 2"
                                />
                            </Td>
                            <Td>
                                <Input
                                    size="sm"
                                    value={editingFields[`${product.wp_product_id}_category`] ?? product.category ?? ''}
                                    onChange={(e) => handleFieldChange(product.wp_product_id, 'category', e.target.value)}
                                    placeholder="Category"
                                />
                            </Td>
                            <Td textAlign="center" width="60px">
                                {product.has_image === true && (
                                    <FaImage color="#4A90E2" size={16} />
                                )}
                            </Td>
                            <Td width="120px" textAlign="right">
                                <Input
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    textAlign="right"
                                    value={editingFields[`${product.wp_product_id}_charge`] ?? product.charge ?? ''}
                                    onChange={(e) => handleFieldChange(product.wp_product_id, 'charge', e.target.value)}
                                    placeholder="Charge"
                                />
                            </Td>
                            <Td>
                                <Switch
                                    size="sm"
                                    isChecked={product.activeyn}
                                    onChange={() => handleToggle(product.wp_product_id, product.activeyn)}
                                />
                            </Td>
                            <Td>
                                <IconButton
                                    size="sm"
                                    aria-label="Save product"
                                    icon={<FaSave />}
                                    onClick={() => handleSaveProduct(product)}
                                    isLoading={updatingProducts.has(product.wp_product_id)}
                                    colorScheme={hasChanges(product.wp_product_id) ? "blue" : "gray"}
                                />
                            </Td>
                            <Td>
                                <Button size="sm" onClick={() => handleViewClick(product.id, product.wp_description_display1)}>
                                    View
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );
};

export default EditStoreProductsTable;